import React from 'react';
import Section from '../Section/Section';
import Typography from '../Typography/Typography';
import QRCode from '../QRCode/QRCode';
import { useAppFeatures } from '../../../modules/ui/selectors/app';
import { Stack } from '../Stack/Stack';
import { useModal } from '../Modal/useModal';
import Spacer from '../Spacer/Spacer';
import Icon from '../Icon/Icon';
import iosInstructions from 'assets/images/ios-instructions.png';
import { useTranslation } from 'utils/translation';
import { useSemaphore } from 'utils/use-semaphore';

const PWAInstructions = () => {
  const modal = useModal();
  const features = useAppFeatures();
  const { acquire, release, isLocked } = useSemaphore();
  const { t } = useTranslation();

  const openImage = () => {
    if (isLocked) {
      // prevent multiple modals from being open
      return;
    }

    acquire();

    modal.open(
      () => (
        <Section style={{ textAlign: 'center' }}>
          <img src={iosInstructions} alt="" />
        </Section>
      ),
      {
        title: t('pwa_instructions_view_ios_instructions'),
        size: 'large',
        onClose: release,
      },
    );
  };

  return (
    <Section>
      <Section>
        <Spacer size={4} />
        <Stack direction="row">
          {features.pwa.url && (
            <QRCode value={features.pwa.url} showLabel={false} />
          )}
          <Stack direction="column">
            <Section>
              <Typography>
                <strong>{t('pwa_instructions_web_address')}</strong>
              </Typography>
              <Typography>
                <a target="_blank" href={features.pwa.url}>
                  {features.pwa.url}
                </a>
              </Typography>
            </Section>
          </Stack>
        </Stack>

        <Stack direction="column">
          <Spacer size={4} />
          <Section modifier="card2" hasBorder>
            <Stack direction="row" align="center">
              <Typography variant="h2" component="strong">
                1.
              </Typography>
              <div style={{ minWidth: '55px' }} />
              <Typography component="strong">
                {t('pwa_instructions_scan_code', { url: features.pwa.url })}
              </Typography>
            </Stack>
          </Section>

          <Spacer size={3} />
          <Section modifier="card2" hasBorder>
            <Stack direction="row" align="center">
              <Typography variant="h2" component="strong">
                2.
              </Typography>
              <div style={{ minWidth: '55px' }} />
              <div>
                <Typography component="strong">
                  {t('pwa_instructions_click_share_icon')}{' '}
                  <Icon name="fa-solid fa-arrow-up-from-bracket" />
                </Typography>
                <Spacer size={1} />
                <Typography
                  variant="label3"
                  color="text-secondary"
                  component="p"
                >
                  {t('pwa_instructions_chrome_location')}
                </Typography>
                <Typography
                  variant="label3"
                  color="text-secondary"
                  component="p"
                >
                  {t('pwa_instructions_safari_location')}
                </Typography>
              </div>
            </Stack>
          </Section>

          <Spacer size={3} />
          <Section modifier="card2" hasBorder>
            <Stack direction="row" align="center">
              <Typography variant="h2" component="strong">
                3.
              </Typography>
              <div style={{ minWidth: '55px' }} />
              <div>
                <Typography component="strong">
                  {t('pwa_instructions_add_to_home_screen')}{' '}
                  <Icon name="fa-regular fa-square-plus" />
                </Typography>
                <Spacer size={1} />
                <Typography
                  variant="label3"
                  color="text-secondary"
                  component="p"
                >
                  {t('pwa_instructions_app_installing')}
                </Typography>
              </div>
            </Stack>
          </Section>

          <Spacer size={3} />
          <Section modifier="card2" hasBorder>
            <Stack direction="row" align="center">
              <Typography variant="h2" component="strong">
                4.
              </Typography>
              <div style={{ minWidth: '55px' }} />
              <div>
                <Typography component="strong">
                  {t('pwa_instructions_close_and_launch')}
                </Typography>
                <Spacer size={1} />
                <Typography
                  variant="label3"
                  color="text-secondary"
                  component="p"
                >
                  {t('pwa_instructions_optimal_experience')}
                </Typography>
              </div>
            </Stack>
          </Section>

          <Spacer size={3} />
          <Section modifier="card-success">
            <Stack direction="row" align="center">
              <Typography variant="h2" component="strong">
                <Icon name="fa-light fa-mobile-notch fa-2x" />
              </Typography>
              <div style={{ minWidth: '45px' }} />
              <div>
                <Typography variant="label3" color="text-primary" component="p">
                  {t('pwa_instructions_iphone_camera_permission')}
                </Typography>
                <Spacer size={2} />
                <Typography variant="label3" color="text-primary" component="p">
                  <a onClick={openImage} className="link">
                    {t('pwa_instructions_view_ios_instructions')}
                  </a>
                </Typography>
              </div>
            </Stack>
          </Section>
        </Stack>
      </Section>
    </Section>
  );
};

export const usePWAInstructions = () => {
  const modal = useModal();
  const { t } = useTranslation();

  return () =>
    modal.open(PWAInstructions, {
      title: t('pwa_instructions_title'),
    });
};

export default PWAInstructions;
